// The back office frame: Za'atar header with numbered stages, KPI strip, then the page.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { navigate } from "../../lib/router.js";
import { signOut, devBypass } from "../../lib/auth.js";
import wordmark from "../../assets/lipari-wordmark-macadamia.svg";
import { StatStrip } from "./ui.jsx";
import InvitationsPage from "./InvitationsPage.jsx";
import EventPage from "./EventPage.jsx";
import AccessPage from "./AccessPage.jsx";
import Agent from "../Agent.jsx";
import HumanPage from "./HumanPage.jsx";
import MonitoringPage from "./MonitoringPage.jsx";

// Flow B's stage order. Tier bands and the show floor arrive with the January work.
const STAGES = [
  { n: 1, label: "Invitations", path: "/admin" },
  { n: 2, label: "Needs a human", path: "/admin/human" },
  { n: 3, label: "Event", path: "/admin/event" },
  { n: 4, label: "Monitoring", path: "/admin/monitoring" },
  { n: 5, label: "Codes", path: "/admin/codes" },
  { n: 6, label: "Access", path: "/admin/access" },
  { n: 7, label: "Show floor", path: "/admin/floor", soon: "January" },
];

export default function AdminShell({ path, who, isAdmin, DevSwitch }) {
  const [data, setData] = useState({ counts: {}, invitations: [] });
  const [codes, setCodes] = useState({ counts: {} });
  const [human, setHuman] = useState(0);
  const load = async () => {
    try { setData(await api("/backoffice/invitations")); } catch { /* view-only may lack some calls */ }
    try { setCodes(await api("/agent/codes?limit=1")); } catch { /* ignore */ }
    try { setHuman((await api("/backoffice/queue")).items.length); } catch { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const c = data.counts ?? {};
  const invited = Object.values(c).reduce((a, b) => a + b, 0);
  const unsent = c.created ?? 0;
  const stage = STAGES.find(s => s.path !== "/admin" && path.startsWith(s.path)) ?? STAGES[0];
  const badges = { "/admin": unsent ? `${unsent} unsent` : null, "/admin/codes": codes.counts?.issued ? `${codes.counts.issued}` : null, "/admin/human": human ? `${human}` : null };

  return (
    <div className="bo">
      <header className="bo-header">
        <a className="brand" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} aria-label="Lipari Food Show, home">
          <img src={wordmark} alt="Lipari" className="wordmark" /><span className="divider" aria-hidden="true" /><span className="sub">FOOD SHOW</span>
        </a>
        <nav className="bo-stages" aria-label="Back office">
          {STAGES.map(s => (
            <button key={s.path} className={`bo-stage ${stage.path === s.path ? "on" : ""} ${s.soon ? "soon" : ""}`} disabled={Boolean(s.soon)} title={s.soon ? `Arrives with the ${s.soon} work` : undefined} onClick={() => navigate(s.path)}>
              <span className="n">{s.n}</span><span>{s.label}</span>{badges[s.path] && <span className="badge">{badges[s.path]}</span>}{s.soon && <span className="soon-tag">{s.soon}</span>}
            </button>
          ))}
        </nav>
        <div className="who">{data.event?.name ? <span className="ev">{data.event.name}</span> : null}{who}{devBypass && DevSwitch && <DevSwitch />}{!devBypass && <button className="ghost signout" onClick={signOut}>Sign out</button>}</div>
      </header>

      <div className="bo-kpis">
        <StatStrip big items={[
          { k: "Invited", v: invited, sub: `${c.created ?? 0} not yet sent` },
          { k: "Opened", v: (c.opened ?? 0) + (c.registered ?? 0), sub: `of ${(c.sent ?? 0) + (c.opened ?? 0) + (c.registered ?? 0)} sent` },
          { k: "Registered", v: c.registered ?? 0, sub: "codes issued", color: "#2f6b3a" },
          { k: "Booked by the agent", v: codes.counts?.booked ?? 0, sub: `of ${codes.counts?.issued ?? 0} codes`, color: "#786018" },
        ]} />
        <div className="bo-kpinote">{data.event?.when && data.event.when !== "dates to be confirmed" ? `${data.event.name}: ${data.event.when}${data.event.location ? `, ${data.event.location}` : ""}.` : `${data.event?.name ?? "Event"}: dates not set yet. Set them under Event before sending invitations.`} Codes for this event: driving {data.event?.codePrefixes?.A ?? "?"}, flying {data.event?.codePrefixes?.B ?? "?"}.</div>
      </div>

      <main className="bo-main">
        {stage.path === "/admin" && <InvitationsPage data={data} reload={load} isAdmin={isAdmin} />}
        {stage.path === "/admin/event" && <EventPage event={data.event} reload={load} isAdmin={isAdmin} />}
        {stage.path === "/admin/access" && <AccessPage isAdmin={isAdmin} />}
        {stage.path === "/admin/codes" && <Agent canBook={isAdmin} embedded />}
        {stage.path === "/admin/human" && <HumanPage isAdmin={isAdmin} reload={load} />}
        {stage.path === "/admin/monitoring" && <MonitoringPage />}
      </main>
    </div>
  );
}
